import { v4 as uuidv4 } from 'uuid';
import { useDebounce } from 'use-debounce';
import { createSelector } from '@reduxjs/toolkit';
import { TypedUseQueryStateResult } from '@reduxjs/toolkit/dist/query/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  appSyncGraphQLApi,
  IDAndParentID,
  OwnerAndGroups,
  useCreateSharedContentBoardMutation,
  useCreateSpacePrivateConfigMutation,
  useGetContentEntriesByParentQuery,
  useGetDistilledMealsByParentQuery,
  useGetMyDayActionRecordsByEnrollmentQuery,
  useGetMyDayActionRecordsBySpaceMembershipQuery,
  useGetNotesByParentQuery,
  useGetPublicSpaceQuery,
  useGetSharedContentBoardsIAMQuery,
  useGetSharedContentBoardsQuery,
  useGetSharedProgrammeQuery,
  useGetSharedProgrammesQuery,
  useGetShoppingListsByProductsBoardQuery,
  useGetSmorgBoardsByIDsQuery,
  useGetSmorgBoardsQuery,
  useGetSpacePrivateConfigBySpaceQuery,
  useGetSpaceQuery,
  useLazyGetDistilledMealsByParentQuery,
  useUpdateSpaceMutation,
} from './smorgAppSyncGraphQLApi';
import {
  ActivityObjectType,
  ContentEntry,
  Meal,
  Note,
  SharedContentEntry,
  SharedMeal,
  ShoppingList,
  SmorgBoard,
  SpaceMembership,
  SpaceMembershipState,
  UserMyDayActionRecord,
  UserProgrammeEnrollment,
} from '../API';
import { mergeMyDayActionRecords } from '../services/programmes';
import {
  currentUserSelector,
  userFriendlyNameForActivitySignalSelector,
} from '../features/user_id/user_id_slice';
import { healthProGroupForSpaceID } from '../services/users';
import { newDbPlannerWeekWithDefaults } from '../services/planner';
import { reportAccountActivitySignalOperation } from '../operations/user_profile_operations';
import {
  SHARED_CONTENT_BOARDS_REFRESH_INTERVAL_SECONDS,
  SHARED_PROGRAMMES_REFRESH_INTERVAL_SECONDS,
  SPACE_REFRESH_INTERVAL_SECONDS,
} from '../services/end_user_published_resources';
import { recentlyLoggedMealsActionRecords } from '../services/my_day';
import { areSearchResultsTheSameMeal } from '../services/meals';

type GetCurrentSpaceMembershipSelectFromResultArg = TypedUseQueryStateResult<
  SpaceMembership[],
  any,
  any
>;

const selectCurrentSpaceMembership = createSelector(
  (res: GetCurrentSpaceMembershipSelectFromResultArg) => res.data,
  (spaceMemberships) => {
    if (spaceMemberships && spaceMemberships.length > 0) {
      const currentSpaceMembership = spaceMemberships.filter((m) =>
        [SpaceMembershipState.ACTIVE, SpaceMembershipState.CANCELLED].includes(
          m.state,
        ),
      )[0];
      return currentSpaceMembership;
    }
    return null;
  },
);

export const useGetCurrentSpaceMembershipQuery = (
  arg?: undefined,
  options?: Parameters<
    typeof appSyncGraphQLApi.useGetSpaceMembershipsByMemberIDQuery
  >[1],
) => {
  const { username: currentUserID } = useSelector(currentUserSelector);

  return appSyncGraphQLApi.useGetSpaceMembershipsByMemberIDQuery(
    currentUserID || '',
    {
      ...options,
      skip: !currentUserID || options?.skip,
      selectFromResult: (result) => ({
        ...result,
        data: selectCurrentSpaceMembership(result),
      }),
    },
  );
};

export const useGetUserProfileQuery = () => {
  const { username: currentUserID } = useSelector(currentUserSelector);

  return appSyncGraphQLApi.useListUserProfileQuery(currentUserID || '', {
    skip: !currentUserID,
  });

  // const { username: currentUserID, groups: memberInCognitoGroups } =
  //   useSelector(currentUserSelector);

  // const queryResult = appSyncGraphQLApi.useListUserProfileQuery(
  //   currentUserID || '',
  //   {
  //     // ...options,
  //     skip: !currentUserID,
  //   },
  // );

  // const [
  //   createUserProfile,
  //   { isLoading: isCreating, isSuccess: isCreatedSuccessfully },
  // ] = useCreateUserProfileMutation();

  // useEffect(() => {
  //   if (!currentUserID) {
  //     return;
  //   }
  //   if (
  //     !queryResult.isLoading &&
  //     !queryResult.data &&
  //     !isCreating &&
  //     !isCreatedSuccessfully
  //   ) {
  //     const isCreator = memberInCognitoGroups.length > 0;
  //     const userProfileDoc = {
  //       userID: currentUserID,
  //     } as CreateUserProfileInput;
  //     if (isCreator) {
  //       userProfileDoc.requestedFeatures = [PROGRAMMES_STUDIO_FEATURE_CODE];
  //       userProfileDoc.enabledFeatures = [PROGRAMMES_STUDIO_FEATURE_CODE];
  //     }
  //     createUserProfile(userProfileDoc).then(() => {
  //       try {
  //         queryResult.refetch(); // re-fetch the query after creation
  //       } catch (error) {
  //         console.log(`Error refetching user profile after creation: ${error}`);
  //       }
  //     });
  //   }
  // }, [
  //   currentUserID,
  //   queryResult.isLoading,
  //   queryResult.data,
  //   createUserProfile,
  //   queryResult.refetch,
  //   queryResult,
  //   memberInCognitoGroups.length,
  //   isCreating,
  //   isCreatedSuccessfully,
  // ]);

  // const memoizedQueryResult = useMemo(() => {
  //   // console.log(`useMemo useGetUserProfileQuery recomputing queryResult`);
  //   return {
  //     ...queryResult,
  //     data: queryResult.data,
  //     isLoading: queryResult.isLoading || isCreating,
  //   };
  // }, [queryResult, isCreating]);

  // return memoizedQueryResult;
};

type GetCurrentProgrammeEnrollmentSelectFromResultArg =
  TypedUseQueryStateResult<UserProgrammeEnrollment[], any, any>;

const selectCurrentProgrammeEnrollment = createSelector(
  (res: GetCurrentProgrammeEnrollmentSelectFromResultArg) => res.data,
  (enrollments) => {
    if (!enrollments) {
      return null;
    }
    const activeProgrammeEnrollments = enrollments.filter(
      (enrollment) => !enrollment.endedAt,
    );
    return activeProgrammeEnrollments[0];
  },
);

export const useCurrentProgrammeEnrollmentQuery = () =>
  appSyncGraphQLApi.useGetUserProgrammeEnrollmentsQuery(undefined, {
    selectFromResult: (result) => ({
      ...result,
      data: selectCurrentProgrammeEnrollment(result),
    }),
  });

type GetMealByIDAndParentSelectFromResultArg = TypedUseQueryStateResult<
  Record<string, Meal>,
  any,
  any
>;

const selectDistilledMealByParentFromDistilledMeals = createSelector(
  (res: GetMealByIDAndParentSelectFromResultArg) => res.data,
  (
    res: GetMealByIDAndParentSelectFromResultArg,
    arg: IDAndParentID | null | undefined,
  ) => arg?.id,
  (meals, mealID) => {
    if (!meals || !mealID) {
      return null;
    }
    return meals[mealID];
  },
);

export const useGetDistilledMealByIDAndParentQuery = (
  arg: IDAndParentID | null | undefined,
  options: Parameters<typeof useGetDistilledMealsByParentQuery>[1],
) => {
  const useGetMealsOptions: typeof options = {
    skip: !arg,
    selectFromResult: (result) => ({
      ...result,
      data: selectDistilledMealByParentFromDistilledMeals(result, arg),
    }),
    ...options,
  };
  return useGetDistilledMealsByParentQuery(
    arg?.parentID || '',
    useGetMealsOptions,
  );
};

type GetSharedMealByIDAndParentSelectFromResultArg = TypedUseQueryStateResult<
  Record<string, SharedMeal>,
  any,
  any
>;

const selectDistilledSharedMealByParentFromDistilledSharedMeals =
  createSelector(
    (res: GetSharedMealByIDAndParentSelectFromResultArg) => res.data,
    (
      res: GetSharedMealByIDAndParentSelectFromResultArg,
      arg: IDAndParentID | null | undefined,
    ) => arg?.id,
    (sharedMeals, sharedMealID) => {
      if (!sharedMeals || !sharedMealID) {
        return null;
      }
      return sharedMeals[sharedMealID];
    },
  );

export const useGetDistilledSharedMealByIDAndParentQuery = (
  arg: IDAndParentID | null | undefined,
  options: Parameters<
    typeof appSyncGraphQLApi.useGetDistilledSharedMealsByParentQuery
  >[1],
) => {
  const useGetSharedMealsOptions: typeof options = {
    skip: !arg,
    selectFromResult: (result) => ({
      ...result,
      data: selectDistilledSharedMealByParentFromDistilledSharedMeals(
        result,
        arg,
      ),
    }),
    ...options,
  };
  return appSyncGraphQLApi.useGetDistilledSharedMealsByParentQuery(
    arg?.parentID || '',
    useGetSharedMealsOptions,
  );
};

type GetSharedContentEntryByIDAndParentSelectFromResultArg =
  TypedUseQueryStateResult<Record<string, SharedContentEntry>, any, any>;

const selectSharedContentEntryByParentFromSharedContentEntries = createSelector(
  (res: GetSharedContentEntryByIDAndParentSelectFromResultArg) => res.data,
  (
    res: GetSharedContentEntryByIDAndParentSelectFromResultArg,
    arg: IDAndParentID | null | undefined,
  ) => arg?.id,
  (sharedContentEntries, sharedContentEntryID) => {
    if (!sharedContentEntries || !sharedContentEntryID) {
      return null;
    }
    return sharedContentEntries[sharedContentEntryID];
  },
);

export const useGetSharedContentEntryByIDAndParentQuery = (
  arg: IDAndParentID | null | undefined,
  options: Parameters<
    typeof appSyncGraphQLApi.useGetSharedContentEntriesByParentQuery
  >[1],
) => {
  const useGetSharedContentEntriesOptions: typeof options = {
    skip: !arg,
    selectFromResult: (result) => ({
      ...result,
      data: selectSharedContentEntryByParentFromSharedContentEntries(
        result,
        arg,
      ),
    }),
  };
  return appSyncGraphQLApi.useGetSharedContentEntriesByParentQuery(
    arg?.parentID || '',
    useGetSharedContentEntriesOptions,
  );
};

type GetNoteByIDAndParentSelectFromResultArg = TypedUseQueryStateResult<
  Record<string, Note>,
  any,
  any
>;

const selectNoteByParentFromNotes = createSelector(
  (res: GetNoteByIDAndParentSelectFromResultArg) => res.data,
  (
    res: GetNoteByIDAndParentSelectFromResultArg,
    arg: IDAndParentID | null | undefined,
  ) => arg?.id,
  (notes, noteID) => {
    if (!notes || !noteID) {
      return null;
    }
    return notes[noteID];
  },
);

export const useGetNoteByIDAndParentQuery = (
  arg: IDAndParentID | null | undefined,
  options: Parameters<typeof useGetNotesByParentQuery>[1],
) => {
  const useGetNotesOptions: typeof options = {
    skip: !arg,
    selectFromResult: (result) => ({
      ...result,
      data: selectNoteByParentFromNotes(result, arg),
    }),
    ...options,
  };
  return useGetNotesByParentQuery(arg?.parentID || '', useGetNotesOptions);
};

type GetContentEntryByIDAndParentSelectFromResultArg = TypedUseQueryStateResult<
  Record<string, ContentEntry>,
  any,
  any
>;

const selectContentEntryByParentFromContentEntries = createSelector(
  (res: GetContentEntryByIDAndParentSelectFromResultArg) => res.data,
  (
    res: GetContentEntryByIDAndParentSelectFromResultArg,
    arg: IDAndParentID | null | undefined,
  ) => arg?.id,
  (contentEntries, contentEntryID) => {
    if (!contentEntries || !contentEntryID) {
      return null;
    }
    return contentEntries[contentEntryID];
  },
);

export const useGetContentEntryByIDAndParentQuery = (
  arg: IDAndParentID | null | undefined,
  options: Parameters<typeof useGetContentEntriesByParentQuery>[1],
) => {
  const useGetContentEntriesOptions: typeof options = {
    skip: !arg,
    selectFromResult: (result) => ({
      ...result,
      data: selectContentEntryByParentFromContentEntries(result, arg),
    }),
    ...options,
  };
  return useGetContentEntriesByParentQuery(
    arg?.parentID || '',
    useGetContentEntriesOptions,
  );
};

type GetShoppingListByIDAndParentSelectFromResultArg = TypedUseQueryStateResult<
  Record<string, ShoppingList>,
  any,
  any
>;

const selectShoppingListByParentFromShoppingLists = createSelector(
  (res: GetShoppingListByIDAndParentSelectFromResultArg) => res.data,
  (
    res: GetShoppingListByIDAndParentSelectFromResultArg,
    arg: IDAndParentID | null | undefined,
  ) => arg?.id,
  (shoppingLists, shoppingListID) => {
    if (!shoppingLists || !shoppingListID) {
      return null;
    }
    return shoppingLists[shoppingListID];
  },
);

export const useGetShoppingListByIDAndParentQuery = (
  arg: IDAndParentID | null | undefined,
  options: Parameters<typeof useGetShoppingListsByProductsBoardQuery>[1],
) => {
  const useGetShoppingListsOptions: typeof options = {
    skip: !arg,
    selectFromResult: (result) => ({
      ...result,
      data: selectShoppingListByParentFromShoppingLists(result, arg),
    }),
    ...options,
  };
  return useGetShoppingListsByProductsBoardQuery(
    arg?.parentID || '',
    useGetShoppingListsOptions,
  );
};

export const useGetSharedProgrammeRecipesBoardIDsForEndUserSearchQuery = () => {
  const enrollmentResult = useCurrentProgrammeEnrollmentQuery();
  const { data: currentProgrammeEnrollment, isLoading: isEnrollmentLoading } =
    enrollmentResult;

  const sharedProgrammeID = currentProgrammeEnrollment?.sharedProgrammeID;

  const sharedProgrammeResult = useGetSharedProgrammeQuery(
    sharedProgrammeID || '',
    {
      skip: !sharedProgrammeID,
      refetchOnMountOrArgChange: SHARED_PROGRAMMES_REFRESH_INTERVAL_SECONDS,
    },
  );
  const { data: sharedProgramme, isLoading: isSharedProgrammeLoading } =
    sharedProgrammeResult;

  const memoizedResult = useMemo(() => {
    const sharedProgrammeAssociatedRecipesBoardIDs =
      sharedProgramme?.recipesBoardIDs || [];

    return {
      ...enrollmentResult,
      data: sharedProgrammeAssociatedRecipesBoardIDs,
      isLoading: isEnrollmentLoading || isSharedProgrammeLoading,
    };
  }, [
    enrollmentResult,
    isEnrollmentLoading,
    isSharedProgrammeLoading,
    sharedProgramme?.recipesBoardIDs,
  ]);

  return memoizedResult;
};

export const useGetSpaceAssignedSharedRecipesBoardIDsForEndUserSearchQuery =
  () => {
    const spaceResult = useGetEndUserSpaceQuery(undefined);

    const { data: currentSpace, isLoading: isSpaceLoading } = spaceResult;

    const memoizedResult = useMemo(() => {
      const spaceAssignedSharedBoardIDs =
        currentSpace?.sharedRecipesBoardIDs || [];

      return {
        ...spaceResult,
        data: spaceAssignedSharedBoardIDs,
        isLoading: isSpaceLoading,
      };
    }, [currentSpace?.sharedRecipesBoardIDs, isSpaceLoading, spaceResult]);

    return memoizedResult;
  };

export const useGetOrCreateSpacePrivateConfigQuery = (
  arg: { spaceID: string; currentHealthProGroup: string | null },
  options: Parameters<
    typeof appSyncGraphQLApi.useGetSpacePrivateConfigBySpaceQuery
  >[1],
) => {
  const queryResult = useGetSpacePrivateConfigBySpaceQuery(
    arg.spaceID,
    options,
  );
  const [createSpacePrivateConfig, { isLoading: isCreating }] =
    useCreateSpacePrivateConfigMutation();
  useEffect(() => {
    if (!queryResult.isLoading && !queryResult.data) {
      // Only call createFoo if foo is not found and not currently loading
      createSpacePrivateConfig({
        spaceID: arg.spaceID,
        groups: arg.currentHealthProGroup ? [arg.currentHealthProGroup] : null,
      }).then(() => {
        queryResult.refetch(); // re-fetch the query after creation
      });
    }
  }, [
    queryResult.isLoading,
    queryResult.data,
    createSpacePrivateConfig,
    arg.spaceID,
    arg.currentHealthProGroup,
    queryResult.refetch,
    queryResult,
  ]);

  const memoizedResult = useMemo(
    () => ({
      ...queryResult,
      isLoading: queryResult.isLoading || isCreating,
    }),
    [isCreating, queryResult],
  );

  return memoizedResult;
};

// export const useGetOrCreateProductsBoardQuery = (
//   arg: undefined,
//   options: Parameters<typeof appSyncGraphQLApi.useListProductsBoardQuery>[1],
// ) => {
//   const spaceMembershipResult = useGetCurrentSpaceMembershipQuery();
//   const {
//     data: currentSpaceMembership,
//     error: spaceMembershipLoadError,
//     isLoading: spaceMembershipLoading,
//   } = spaceMembershipResult;

//   const currentSpaceMembershipID = currentSpaceMembership?.id;

//   const queryResult = useListProductsBoardQuery(undefined, options);
//   const [createProductsBoard, { isLoading: isCreating }] =
//     useCreateProductsBoardMutation();
//   useEffect(() => {
//     if (
//       currentSpaceMembershipID &&
//       !queryResult.isLoading &&
//       !queryResult.data
//     ) {
//       // Only call createFoo if foo is not found and not currently loading
//       createProductsBoard(
//         newProductsBoardWithDefaultFields(currentSpaceMembershipID),
//       ).then(() => {
//         queryResult.refetch(); // re-fetch the query after creation
//       });
//     }
//   }, [
//     currentSpaceMembershipID,
//     queryResult.isLoading,
//     queryResult.data,
//     createProductsBoard,
//     queryResult.refetch,
//     queryResult,
//   ]);

//   const memoizedResult = useMemo(
//     () => ({
//       ...queryResult,
//       isLoading: queryResult.isLoading || isCreating,
//     }),
//     [isCreating, queryResult],
//   );

//   return memoizedResult;
// };

export const useGetOwnedRecipesBoardsQuery = (
  _unusedArg?: undefined,
  options?: Parameters<typeof useGetSmorgBoardsQuery>[1],
) => {
  const { username, groups } = useSelector(currentUserSelector);

  const underlyingArg = useMemo(
    () => ({ username: username || '', groups } as OwnerAndGroups),
    [groups, username],
  );
  const underlyingOptions = {
    ...(options || {}),
    skip: !username,
  };

  // work around 'Parameter 'recipesBoard' implicitly has an 'any' type' downstream
  const result = useGetSmorgBoardsQuery(underlyingArg, underlyingOptions) as {
    data?: SmorgBoard[] | undefined;
  };

  return result;
};

export const useGetOrCreateSpaceContentBoardsQuery = (
  _unusedArg?: null,
  options?: Parameters<typeof appSyncGraphQLApi.useGetSpaceQuery>[1],
) => {
  const spaceQueryResult = useGetSpaceQuery(undefined, options);

  const { data: healthProSpace } = spaceQueryResult;

  const currentHealthProGroup =
    healthProSpace && healthProGroupForSpaceID(healthProSpace.id);

  const sharedContentBoardsQueryResult = useGetSharedContentBoardsQuery(
    healthProSpace?.spaceSharedContentBoardIDs,
    {
      skip:
        !healthProSpace ||
        !healthProSpace.spaceSharedContentBoardIDs ||
        healthProSpace.spaceSharedContentBoardIDs.length === 0,
    },
  );

  const [createSharedContentBoard, { isLoading: isCreating }] =
    useCreateSharedContentBoardMutation();

  const [updateSpace, { isLoading: isUpdatingSpace }] =
    useUpdateSpaceMutation();

  const friendlyUsername = useSelector(
    userFriendlyNameForActivitySignalSelector,
  );

  useEffect(() => {
    if (
      spaceQueryResult.isLoading ||
      sharedContentBoardsQueryResult.isLoading ||
      (healthProSpace.spaceSharedContentBoardIDs || []).length > 0
    ) {
      return;
    }
    createSharedContentBoard({
      id: uuidv4(),
      title: 'App content',
      groups: currentHealthProGroup && [currentHealthProGroup],
      lanes: [],
    }).then(({ data: createdSharedContentBoard }) => {
      if (!createdSharedContentBoard) {
        throw new Error('Could not create shared content board');
      }
      reportAccountActivitySignalOperation({
        spaceID: healthProSpace.id,
        friendlyUsername,
        lastActivityObjectType: ActivityObjectType.SHARED_CONTENT_BOARDS,
        lastActivityObjectID: createdSharedContentBoard.id,
      });
      const updatedSpace = {
        ...healthProSpace,
        spaceSharedContentBoardIDs: [
          ...(healthProSpace.spaceSharedContentBoardIDs || []),
          createdSharedContentBoard.id,
        ],
      };
      return updateSpace(updatedSpace).then(() =>
        sharedContentBoardsQueryResult.refetch(),
      );
    });
  }, [
    createSharedContentBoard,
    currentHealthProGroup,
    friendlyUsername,
    healthProSpace,
    sharedContentBoardsQueryResult,
    spaceQueryResult.isLoading,
    updateSpace,
  ]);

  const memoizedResult = useMemo(
    () => ({
      ...sharedContentBoardsQueryResult,
      isLoading:
        sharedContentBoardsQueryResult.isLoading ||
        spaceQueryResult.isLoading ||
        isCreating ||
        isUpdatingSpace,
    }),
    [
      isCreating,
      isUpdatingSpace,
      sharedContentBoardsQueryResult,
      spaceQueryResult.isLoading,
    ],
  );

  return memoizedResult;
};

export const useGetHealthProBoardsQuery = (
  arg?: null,
  options?: Parameters<typeof appSyncGraphQLApi.useGetSmorgBoardsByIDsQuery>[1],
) => {
  const idsResult = useGetSharedProgrammeRecipesBoardIDsForEndUserSearchQuery();

  const {
    data: programmeRecipesBoardsIDs,
    isLoading: isProgrammeRecipesBoardsIDsLoading,
  } = idsResult;

  const boardsResult = useGetSmorgBoardsByIDsQuery(programmeRecipesBoardsIDs, {
    ...(options || {}),
    skip:
      isProgrammeRecipesBoardsIDsLoading ||
      !programmeRecipesBoardsIDs ||
      programmeRecipesBoardsIDs.length === 0,
    refetchOnMountOrArgChange: SHARED_PROGRAMMES_REFRESH_INTERVAL_SECONDS,
  });

  const presentBoardsResult = useMemo(() => {
    const presentBoards =
      boardsResult.data && boardsResult.data.filter(Boolean);
    return {
      ...boardsResult,
      data: presentBoards,
    };
  }, [boardsResult]);

  return presentBoardsResult;
};

export const useLazyGetOrCreateDbPlannerWeekByStartDateQuery = () => {
  const [triggerGet, getResult] =
    appSyncGraphQLApi.useLazyGetDbPlannerWeekQuery();
  const [createPlannerWeek, createResult] =
    appSyncGraphQLApi.useCreateDbPlannerWeekMutation();

  const { data: currentSpaceMembership } = useGetCurrentSpaceMembershipQuery();

  const ensurePlannerWeek = useCallback(
    async (startDate: string) => {
      // Trigger the lazy query
      const result = await triggerGet(startDate, true);
      if (result.data && !result.isLoading) {
        return result;
      }
      // If not found, create it
      const createInput = newDbPlannerWeekWithDefaults(
        startDate,
        currentSpaceMembership?.id,
      );
      const createMutationResult = await createPlannerWeek(createInput);
      return createMutationResult;
    },
    [triggerGet, currentSpaceMembership?.id, createPlannerWeek],
  );

  return [
    ensurePlannerWeek,
    {
      ...(getResult || {}),
      ...(createResult || {}),
    },
  ];
};

export const useGetEndUserSpaceQuery = (
  _arg?: undefined,
  options?: Parameters<typeof appSyncGraphQLApi.useGetPublicSpaceQuery>[1],
) => {
  const { data: currentSpaceMembership } = useGetCurrentSpaceMembershipQuery(
    undefined,
    { skip: options?.skip },
  );

  return useGetPublicSpaceQuery(currentSpaceMembership?.spaceID || '', {
    skip: options?.skip || !currentSpaceMembership?.spaceID,
    refetchOnMountOrArgChange: SPACE_REFRESH_INTERVAL_SECONDS,
  });
};

export const useGetSharedContentBoardsForEndUserQuery = (
  arg?: null,
  options?: Parameters<
    typeof appSyncGraphQLApi.useGetSharedContentBoardsIAMQuery
  >[1],
) => {
  const { data: currentSpace } = useGetEndUserSpaceQuery();

  return useGetSharedContentBoardsIAMQuery(
    currentSpace?.spaceSharedContentBoardIDs || [],
    {
      skip: !currentSpace,
      refetchOnMountOrArgChange: SHARED_CONTENT_BOARDS_REFRESH_INTERVAL_SECONDS,
    },
  );
};

export const useGetSharedProgrammesForEndUserQuery = (
  arg?: null,
  options?: Parameters<
    typeof appSyncGraphQLApi.useGetSharedContentBoardsIAMQuery
  >[1],
) => {
  const { data: currentSpaceMembership } = useGetCurrentSpaceMembershipQuery(
    undefined,
    { skip: options?.skip },
  );

  return useGetSharedProgrammesQuery(currentSpaceMembership?.spaceID || '', {
    skip: !currentSpaceMembership,
    refetchOnMountOrArgChange: SHARED_PROGRAMMES_REFRESH_INTERVAL_SECONDS,
  });
};

export const useGetRecentlyLoggedDistilledMealsQuery = () => {
  const { data: currentSpaceMembership } = useGetCurrentSpaceMembershipQuery();

  const { data: currentProgrammeEnrollment } =
    useCurrentProgrammeEnrollmentQuery();

  const { data: myDayActionRecordsByEnrollment } =
    useGetMyDayActionRecordsByEnrollmentQuery(
      currentProgrammeEnrollment?.id || '',
      {
        skip: !currentProgrammeEnrollment?.id,
      },
    );

  const { data: myDayActionRecordsBySpaceMembership } =
    useGetMyDayActionRecordsBySpaceMembershipQuery(
      currentSpaceMembership?.id || '',
      {
        skip: !currentSpaceMembership?.id,
      },
    );

  const myDayActionRecords = mergeMyDayActionRecords(
    myDayActionRecordsBySpaceMembership || [],
    myDayActionRecordsByEnrollment || [],
  );

  const actionRecords = recentlyLoggedMealsActionRecords(
    myDayActionRecords,
  ) as Array<UserMyDayActionRecord>;

  const mealIDsWithParentIDs = actionRecords.map((ar) => ({
    mealID: ar.objectID,
    parentID: ar.parentID,
  }));
  console.log({ mealIDsWithParentIDs });

  const [lazyGetDistilledMealsByParent] =
    useLazyGetDistilledMealsByParentQuery();

  const [debouncedStringifiedMealIDsWithParents] = useDebounce(
    JSON.stringify(mealIDsWithParentIDs),
    2000,
  );

  const [recentlyLoggedDistilledMeals, setRecentlyLoggedDistilledMeals] =
    useState<Array<Meal>>([]);

  useEffect(() => {
    if (mealIDsWithParentIDs && mealIDsWithParentIDs.length > 0) {
      const promises = mealIDsWithParentIDs.map(
        async ({
          mealID,
          parentID,
        }: {
          mealID: string;
          parentID: string | null | undefined;
        }) => {
          if (!mealID || !parentID) {
            return null;
          }
          const { data: distilledMeals } = await lazyGetDistilledMealsByParent(
            parentID || '',
            true,
          );
          return distilledMeals && distilledMeals[mealID];
        },
      );
      Promise.all(promises).then((meals) => {
        const presentMeals = meals.filter(Boolean) as Array<Meal>;
        const uniqueMeals: Array<Meal> = [];
        // eslint-disable-next-line no-restricted-syntax
        for (const meal of presentMeals) {
          if (
            !uniqueMeals.some((existingMeal) =>
              areSearchResultsTheSameMeal(
                existingMeal.recipes[0],
                meal.recipes[0],
              ),
            )
          ) {
            uniqueMeals.push(meal);
          }
        }
        setRecentlyLoggedDistilledMeals(uniqueMeals);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedStringifiedMealIDsWithParents, lazyGetDistilledMealsByParent]);

  return { data: recentlyLoggedDistilledMeals, isLoading: false };
};
